import { useState, useCallback } from 'react';
import { Download, Copy, ShieldAlert, Check } from 'lucide-react';
import { palette, admin, transition, fontFamily } from '../../../styles/tokens';
import { SettingsSubPage } from '../components/SettingsSubPage';
import { Toast } from '../../../components/common/Toast';
import { useConfigExport, EXPORT_SECTION_DEFS } from '../hooks/useConfigExport';

function Toggle({ on, onToggle, label }: { on: boolean; onToggle: () => void; label?: string }) {
  return (
    <div
      role="switch"
      aria-checked={on}
      aria-label={label}
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          onToggle();
        }
      }}
      style={{
        width: 36, height: 20, borderRadius: 10, cursor: 'pointer',
        background: on ? palette.accent : palette.g300,
        padding: 2, display: 'flex', alignItems: 'center',
        transition: `background ${transition.fast}`,
        outline: 'none',
      }}
    >
      <div style={{
        width: 16, height: 16, borderRadius: 8,
        background: palette.white,
        marginLeft: on ? 16 : 0,
        transition: `margin-left ${transition.fast}`,
      }} />
    </div>
  );
}

export function ExportConfigPage() {
  const { sections, toggleSection, exportConfig, copyToClipboard, loading, error } = useConfigExport();
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [copied, setCopied] = useState(false);

  const lastExport = (() => {
    try {
      const v = localStorage.getItem('crewbit-last-export');
      if (v) return new Date(v).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch { /* ignore */ }
    return null;
  })();

  const handleDownload = useCallback(async () => {
    await exportConfig();
    setToast({ message: 'Config downloaded', type: 'success' });
  }, [exportConfig]);

  const handleCopy = useCallback(async () => {
    await copyToClipboard();
    setCopied(true);
    setToast({ message: 'Copied to clipboard', type: 'success' });
    setTimeout(() => setCopied(false), 2000);
  }, [copyToClipboard]);

  return (
    <SettingsSubPage title="Export config" description="Download your settings as a portable JSON file">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Toggle list */}
        <div style={{
          background: palette.white,
          borderRadius: admin.cardRadius,
          border: `1px solid ${admin.border}`,
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '14px 20px',
            borderBottom: `1px solid ${palette.g100}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>
              What's included
            </span>
            {lastExport && (
              <span style={{ fontSize: 11, color: palette.g400 }}>
                Last exported: {lastExport}
              </span>
            )}
          </div>
          {EXPORT_SECTION_DEFS.map((def, i) => (
            <div key={def.key} style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 20px',
              borderBottom: i < EXPORT_SECTION_DEFS.length - 1 ? `1px solid ${palette.g50}` : 'none',
            }}>
              <span style={{ fontSize: 13.5, color: palette.g700 }}>{def.label}</span>
              <Toggle on={sections[def.key]} onToggle={() => toggleSection(def.key)} label={`Include ${def.label}`} />
            </div>
          ))}
        </div>

        {/* Security note */}
        <div style={{
          background: palette.g50,
          borderRadius: admin.cardRadius,
          border: `1px solid ${palette.g150}`,
          padding: '14px 20px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
        }}>
          <ShieldAlert size={16} color={palette.g400} style={{ marginTop: 1, flexShrink: 0 }} />
          <div style={{ fontSize: 12.5, color: palette.g500, lineHeight: 1.5 }}>
            <strong style={{ color: palette.g600 }}>Never exported:</strong>{' '}
            passwords, Stripe secrets, payment history, invoices, client data, messages
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            padding: '10px 16px', borderRadius: 8,
            background: palette.redWash, border: `1px solid ${palette.red}`,
            fontSize: 13, color: palette.red,
          }}>
            {error}
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={handleDownload}
            disabled={loading}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '10px 20px', borderRadius: 8,
              border: 'none', background: loading ? palette.accentDeep : palette.accent,
              fontSize: 13, fontWeight: 600, color: palette.white,
              cursor: loading ? 'progress' : 'pointer', fontFamily,
              transition: `background ${transition.fast}`,
            }}
          >
            <Download size={14} strokeWidth={2} />
            {loading ? 'Exporting…' : 'Download config JSON'}
          </button>
          <button
            onClick={handleCopy}
            disabled={loading}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '10px 20px', borderRadius: 8,
              border: `1px solid ${admin.border}`,
              background: palette.white,
              fontSize: 13, fontWeight: 500,
              color: copied ? palette.green : palette.g600,
              cursor: loading ? 'default' : 'pointer', fontFamily,
              transition: `all ${transition.fast}`,
            }}
          >
            {copied ? <Check size={14} /> : <Copy size={14} strokeWidth={1.8} />}
            {copied ? 'Copied' : 'Copy to clipboard'}
          </button>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={!!toast}
          onDismiss={() => setToast(null)}
        />
      )}
    </SettingsSubPage>
  );
}
